import React, { useEffect, useMemo, useRef, useState } from "react";
import RoundLoader from "../../../../components/ui/RoundLoader";
import { useSyncScrollbarWidth } from "../../hooks/useSyncScrollbarWidth";
import WatermarkThumbMarks from "../../watermark/Popup/WatermarkThumbMarks";
import { PDF_WATERMARK_MAX_PAGES } from "./renderPdfFirstPage";
import "./DocumentPageThumbnails.css";

function ThumbCard({
  pageNumber,
  imageUrl,
  active,
  onSelect,
  onNeedPage,
  disabled,
  watermark = null,
  showMarks = true,
  MarksComponent = WatermarkThumbMarks,
  status = null,
}) {
  const rootRef = useRef(null);
  const frameRef = useRef(null);
  const askedRef = useRef(false);
  const [frameSize, setFrameSize] = useState({ width: 180, height: 240 });

  useEffect(() => {
    askedRef.current = false;
  }, [pageNumber, imageUrl]);

  useEffect(() => {
    if (imageUrl || !onNeedPage) return undefined;
    const el = rootRef.current;
    if (!el) return undefined;

    const ask = () => {
      if (askedRef.current) return;
      askedRef.current = true;
      onNeedPage(pageNumber);
    };

    if (typeof IntersectionObserver === "undefined") {
      ask();
      return undefined;
    }

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) ask();
      },
      { root: el.closest(".dpt"), rootMargin: "80px 0px", threshold: 0.01 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [imageUrl, onNeedPage, pageNumber]);

  useEffect(() => {
    const el = frameRef.current;
    if (!el) return undefined;
    const measure = () => {
      const width = el.clientWidth;
      const height = el.clientHeight;
      if (!width || !height) return;
      setFrameSize((prev) =>
        prev.width === width && prev.height === height
          ? prev
          : { width, height }
      );
    };
    measure();
    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, []);

  const select = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (disabled) return;
    onSelect?.(pageNumber);
  };

  return (
    <button
      type="button"
      ref={rootRef}
      className={`dpt__thumb${active ? " is-active" : ""}`}
      onClick={select}
      onPointerDown={(e) => {
        // Keep rail clicks from being swallowed by preview drag/capture handlers.
        if (e.button === 0) e.stopPropagation();
      }}
      disabled={disabled}
      aria-label={`Page ${pageNumber}`}
      aria-current={active ? "page" : undefined}
    >
      <span className="dpt__frame" ref={frameRef}>
        {imageUrl ? (
          <>
            <img src={imageUrl} alt="" className="dpt__image" draggable={false} />
            {showMarks && watermark && MarksComponent ? (
              (Array.isArray(watermark) ? watermark : [watermark])
                .filter(Boolean)
                .map((wm, i) => (
                  <MarksComponent
                    key={wm?.signPdfMarkId || `wm-thumb-${i}`}
                    mark={wm}
                    watermark={wm}
                    width={frameSize.width}
                    height={frameSize.height}
                  />
                ))
            ) : null}
            {status && status !== "empty" ? (
              <span
                className={`dpt__status dpt__status--${status}`}
                title={
                  status === "signed"
                    ? "Signed"
                    : status === "partial"
                      ? "Fields placed — add a signature"
                      : undefined
                }
                aria-hidden="true"
              />
            ) : null}
          </>
        ) : (
          <span className="dpt__placeholder">
            <RoundLoader size="sm" />
          </span>
        )}
      </span>
      <span className="dpt__num">{pageNumber}</span>
    </button>
  );
}

/**
 * Narrow right-rail of scrollable page thumbnails.
 * Optional watermark / getPageWatermark mirrors live marks on every page.
 * Tools can override MarksComponent (e.g. Sign PDF → SignPdfThumbMarks).
 */
const DocumentPageThumbnails = ({
  pageCount = 0,
  pageUrls = {},
  currentPage = 1,
  onSelect = null,
  ensurePageUrl = null,
  disabled = false,
  maxPages = PDF_WATERMARK_MAX_PAGES,
  /** Shared watermark (text / logo / signature). */
  watermark = null,
  /** Per-page watermark resolver (page numbers). Overrides `watermark` when set. */
  getPageWatermark = null,
  /** Hide marks in Original compare mode. */
  showMarks = true,
  /** Mark compositor; defaults to watermark thumbs. */
  MarksComponent = WatermarkThumbMarks,
  /** Optional per-page status: (pageNum) => "empty" | "partial" | "signed" | null */
  getPageStatus = null,
}) => {
  const scrollRef = useRef(null);
  const total = Math.min(Math.max(0, pageCount), maxPages);
  const pages = useMemo(
    () => Array.from({ length: total }, (_, i) => i + 1),
    [total]
  );

  useSyncScrollbarWidth(scrollRef, [total, pageUrls, currentPage, watermark], {
    enabled: total >= 2,
  });

  if (total < 2) return null;

  return (
    <aside className="dpt" aria-label="Document pages">
      <div className="dpt__scroll" ref={scrollRef}>
        {pages.map((n) => {
          const pageMark =
            typeof getPageWatermark === "function"
              ? getPageWatermark(n)
              : watermark;
          const status =
            typeof getPageStatus === "function" ? getPageStatus(n) : null;
          return (
            <ThumbCard
              key={n}
              pageNumber={n}
              imageUrl={pageUrls[n] || null}
              active={n === currentPage}
              onSelect={onSelect}
              onNeedPage={ensurePageUrl}
              disabled={disabled}
              watermark={pageMark}
              showMarks={showMarks}
              MarksComponent={MarksComponent}
              status={status}
            />
          );
        })}
      </div>
    </aside>
  );
};

export default DocumentPageThumbnails;
